import { SelectItem } from '@/components';
import {
  IAddress,
  IAgency,
  ICFS,
  IIncidentCode,
  IKaseInvolvement,
  IMasterName,
  IMasterNameReq,
  IMasterNameOrganization,
  IMasterVehicle,
  ISupervisor,
  ITask,
  IUser,
  PropertyVehicleInvolvement,
} from '@/models';

import { IAuditModel, ITimestampsModel, ObjectId } from './base';
import { IDomesticIncidentReport } from './domestic.incident.report';
import { IFile } from './file';
import { IVendor } from './vendors';

export enum KaseStatus {
  OPEN = 'OPEN',
  WAITING_FOR_SUPERVISOR = 'WAITING_FOR_SUPERVISOR',
  APPROVED_BY_SUPERVISOR = 'APPROVED_BY_SUPERVISOR',
  REJECTED = 'REJECTED',
  ARCHIVED = 'ARCHIVED',
}

export enum AttemptIndicator {
  ATTEMPTED = 'A',
  COMPLETED = 'C',
}

export enum BiasMotivation {
  // Race
  ANTI_WHITE = '11',
  ANTI_BLACK_AFRICAN_AMERICAN = '12',
  ANTI_AMERICAN_INDIAN_ALASKA_NATIVE = '13',
  ANTI_ASIAN = '14',
  ANTI_MULTIPLE_RACES_GROUP = '15',
  ANTI_NATIVE_HAWAIIAN_OTHER_PACIFIC_ISLANDER = '16',

  // Religion
  ANTI_RELIGIOUS_PRACTICE = '20',
  ANTI_JEWISH = '21',
  ANTI_ROMAN_CATHOLIC = '22',
  ANTI_PROTESTANT = '23',
  ANTI_ISLAMIC = '24',
  ANTI_MULTIPLE_RELIGIONS_GROUP = '26',
  ANTI_ATHEISM_AGNOSTICISM = '27',
  ANTI_MORMON = '28',
  ANTI_OTHER_RELIGION = '29',
  ANTI_JEHOVAH_WITNESS = '30',
  ANTI_EASTERN_ORTHODOX = '81',
  ANTI_OTHER_CHRISTIAN = '82',
  ANTI_BUDDHIST = '83',
  ANTI_HINDU = '84',
  ANTI_SIKH = '85',

  // Ethnicity,national origin, ancestry
  ANTI_HISPANIC_LATINO = '31',
  ANTI_ARAB = '32',
  ANTI_OTHER_RACE_ETHNICITY_ANCESTRY = '34',
  ANTI_NOT_HISPANIC_LATINO = '35',
  ANTI_OTHER_ORIGIN = '36',

  // Sexual Orientation
  ANTI_GAY = '41',
  ANTI_LESBIAN = '42',
  ANTI_LESBIAN_GAY_BISEXUAL_TRANSGENDER = '43',
  ANTI_HETEROSEXUAL = '44',
  ANTI_BISEXUAL = '45',

  // Other
  ANTI_OLD_AGE = '04',
  ANTI_PHYSICAL_DISABILITY = '51',
  ANTI_MENTAL_DISABILITY = '52',
  ANTI_MALE = '61',
  ANTI_FEMALE = '62',
  ANTI_TRANSGENDER = '71',
  ANTI_GENDER_NON_CONFORMING = '72',
  NOT_APPLICABLE = '77',
  NOT_REPORTED_BY_AGENCY = '88',
  UNKNOWN = '99',
}
export const KaseTypes = {
  INCIDENT_REPORT: 'Incident Report',
  NARRATIVE_ONLY_REPORT: 'Narrative Only',
  FIELD_INTERVIEW_REPORT: 'Field Interview',
} as const;
export type IKaseType = keyof typeof KaseTypes;

export const DispositionReasons = [
  { group: 'Arrest', label: 'Arrest', value: '' },
  { label: '01 = Arrest-Adult', value: '01' },
  { label: '02 = Arrest-Juvenile', value: '02' },
  { group: 'Exceptional Means', label: 'Exceptional Means', value: '' },
  { label: '10 = Death of Offender', value: '10' },
  { label: '11 = Prosecution Declined', value: '11' },
  { label: '12 = Extradition Denied', value: '12' },
  { label: '13 = Victim Refuses to Cooperate', value: '13' },
  { label: '14 = No Court Referral (Juvenile Only)', value: '14' },
  { group: 'Other', label: 'Other', value: '' },
  { label: '20 = Warrant Issued', value: '20' },
  { label: '21 = Investigation Pending', value: '21' },
  { label: '20 = Closed', value: '22' },
  { label: '88 = Not Reported', value: '88' },
  { label: '99 = Unknown', value: '99' },
] as const satisfies readonly SelectItem[];

export type IDispositionReason = keyof typeof DispositionReasons;
export interface IDisposition {
  reason: IDispositionReason;
  createdBy: IUser;
  comment: string;
  createdAt: Date;
  exceptionalClearanceDate?: Date;
}
export interface IIncidentInfoForm {
  biasMotivations: BiasMotivation[];
  involveCargoTheft: boolean;
}

export interface IAssignmentInfo {
  // KASE_APPROVER who will be responsible for case approval
  // As default, case will be assigned to supervisor of case submitter
  // It can be reassigned to other KASE_APPROVER
  assignedTo?: IApprover;
  comment?: string | null;
  assignedAt?: Date | null;
}
export interface IPriorityInfo {
  isPriority: boolean;
  comment?: string;
  updatedBy?: ISupervisor;
  updatedAt?: Date;
}

export interface IKase extends IAuditModel, IIncidentInfoForm {
  number: string;
  status: KaseStatus;
  createdBy: IUser;
  address?: IAddress;
  incidentStartedAt?: Date;
  incidentStartedAtOption?: string;
  incidentReportedAt?: Date;
  incidentReportedAtTimeOption?: string;
  incidentEndedAt?: Date;
  isSentToState: boolean;
  sentToStateAt?: Date;
  isSentToNIBRS: boolean;
  sentToNIBRSAt?: Date;
  isExcludedFromNibrs?: boolean;
  files?: IFile[];
  primaryOfficer: IUser;
  assistingOfficers?: IUser[];
  agency: IAgency;
  incidentCodes?: string[];
  currentDisposition?: IDisposition;
  dispositions?: IDisposition[];
  rejectedInfo?: IKaseRejection[];
  primaryInvestigator?: IUser;
  type: IKaseType;
  approvedInfo?: Array<IApprovedInfo>;
  priorityInfo: IPriorityInfo;
  priorityComment: string;
  assignmentInfo?: IAssignmentInfo;
  locationCode?: ICTVCode;
  sdpIdentifier?: string;
  cfsParent?: string;
  cfsNumber?: string;
  cfsShortDescription?: string;
  requireNarative?: string[];
  drafts?: IDraft[];
  usersWithCaseAccess?: IUser[];
  caseRestrictedBy?: IUser;
}

export interface IDraft extends ObjectId, ITimestampsModel {
  draftContent: IDomesticIncidentReport;
}

export interface IKaseVehicle extends IAuditModel {
  plateNumber: string;
  plateState: string;
  color: string;
  make: string;
  model: string;
  vin: string;
  year: string;
  impounded: boolean;
}

export interface IKaseNewForm {
  _id?: string;
  incidentStartedAt: Date;
  incidentStartedAtOption?: string;
  incidentReportedAt?: Date;
  incidentReportedAtTimeOption?: string;
  incidentEndedAt?: Date;
  primaryOfficer: string;
  primaryInvestigator: string;
  assistingOfficers: string[];
  incidentCodes: string[];
  address: IAddress;
  type: IKaseType;
  locationCode?: string;
  sdpIdentifier?: string;
  requireNarative?: Record<string, boolean>;
}

export interface IKaseCreateRequest {
  _id?: string;
  address: string;
  incidentStartedAt: string;
  incidentEndedAt?: string;
  primaryOfficer: string;
  primaryInvestigator: string;
  incidentCodes: string[];
  type: IKaseType;
  locationCode?: string;
  sdpIdentifier?: string;
  requireNarative?: string[];
}

export interface IKaseMasterName extends IAuditModel {
  kaseId: string;
  masterName: IMasterName | IMasterNameOrganization;
  relationship: IKaseInvolvement;
  registeredOwnerVehicles?: string[];
  additionalInfo?: any; // TODO: this needs to be properly typed
}

export interface IKaseRelatedCounts {
  names: number;
  vehicles: number;
  offenses: number;
  arrestees: number;
  narratives: number;
  properties: number;
}

export interface IKaseCfsLink extends ITimestampsModel, ObjectId {
  kaseId: string;
  cfs: ICFS;
}

export interface IKaseTaskLink extends ITimestampsModel, ObjectId {
  kaseId: string;
  task: ITask;
}

interface IRelatedOffense extends ObjectId {
  number: string;
  offenderVictims: {
    offender?: string;
    victims?: {
      victim: string;
      relationship: string;
    }[];
  }[];
}

export interface IKaseMasterNameAggregateResult extends ObjectId {
  kase: string;
  masterName: string;
  masterNameDetails: {
    name: string;
    firstName?: string;
    lastName?: string;
    gender?: string;
  };

  additionalInfo?: any;

  relationship?: {
    behavior?: string;
    involvement?: string[];
  };

  relatedOffenses: IRelatedOffense[];
}

export interface IKaseKaseLink extends ITimestampsModel, ObjectId {
  linked: ObjectId & {
    number: string;
    address: IAddress;
    incidentReportedAt: Date;
    incidentCodes: IIncidentCode[];
    primaryOfficer?: IUser | string;
  };
  main: ObjectId & { number: string };
}

export interface IKaseSentForApprovalRequest {
  comment: string;
}
export interface IKaseRejectRequest {
  reason: string;
}

export interface IKaseMasterVehicle extends IAuditModel {
  kaseId: string;
  masterVehicle: IMasterVehicle | null;
  disposition?: PropertyVehicleInvolvement;
}

export enum MasterNameInvolvement {
  AIDED = 'AI',
  BUSINESS = 'BS',
  CALLER = 'CA',
  CODE_ENFORCEMENT = 'CF',
  CHILD = 'CH',
  COMPLEX = 'CM',
  COMPLAINANT = 'CO',
  CORONER = 'CR',
  COURT = 'CT',
  COMPLAINANT_VICTIM = 'CV',
  DISTRICT_ATTORNEY = 'DA',
  DECEASED = 'DC',
  DEFENDANT = 'DE',
  DRIVER_OWNER = 'DO',
  DRIVER = 'DR',
  EMS = 'EM',
  FIRE_DEPARTMENT = 'FD',
  HOSPITAL = 'HS',
  HOTEL = 'HT',
  JUDGE = 'JD',
  JUVENILE_SUSPECT = 'JS',
  JUVENILE = 'JV',
  MISSING_PERSON = 'MP',
  NOT_INTERVIEWED = 'NI',
  OFFICER = 'OF',
  OTHER = 'OT',
  OWNER = 'OW',
  PARENT = 'PA',
  POLICE_DEPARTMENT = 'PD',
  PETITIONER = 'PE',
  PERSON_INTERVIEWED = 'PI',
  PERSON_REPORTING = 'PR',
  PASSENGER = 'PS',
  RESPONDENT = 'RE',
  SCHOOL = 'SC',
  SEX_OFFENDER = 'SO',
  SERVED_PERSON = 'SP',
  SUSPECT = 'SU',
  TEST = 'TE',
  TOW_COMPANY = 'TW',
  VICTIM = 'VI',
  WITNESS = 'WI',
}

export const KaseMasterNameInvolvementLabels: Record<
  MasterNameInvolvement,
  string
> = {
  [MasterNameInvolvement.AIDED]: 'Aided',
  [MasterNameInvolvement.BUSINESS]: 'Business',
  [MasterNameInvolvement.CALLER]: 'Caller',
  [MasterNameInvolvement.CODE_ENFORCEMENT]: 'Code Enforcement',
  [MasterNameInvolvement.CHILD]: 'Child',
  [MasterNameInvolvement.COMPLEX]: 'Complex',
  [MasterNameInvolvement.COMPLAINANT]: 'Complainant',
  [MasterNameInvolvement.CORONER]: 'Coroner',
  [MasterNameInvolvement.COURT]: 'Court',
  [MasterNameInvolvement.COMPLAINANT_VICTIM]: 'Complainant / Victim',
  [MasterNameInvolvement.DISTRICT_ATTORNEY]: 'District Attorney',
  [MasterNameInvolvement.DECEASED]: 'Deceased',
  [MasterNameInvolvement.DEFENDANT]: 'Defendant',
  [MasterNameInvolvement.DRIVER_OWNER]: 'Driver Owner',
  [MasterNameInvolvement.DRIVER]: 'Driver',
  [MasterNameInvolvement.EMS]: 'EMS',
  [MasterNameInvolvement.FIRE_DEPARTMENT]: 'Fire Department',
  [MasterNameInvolvement.HOSPITAL]: 'Hospital',
  [MasterNameInvolvement.HOTEL]: 'Hotel',
  [MasterNameInvolvement.JUDGE]: 'Judge',
  [MasterNameInvolvement.JUVENILE_SUSPECT]: 'Juvenile Suspect',
  [MasterNameInvolvement.JUVENILE]: 'Juvenile',
  [MasterNameInvolvement.MISSING_PERSON]: 'Missing Person',
  [MasterNameInvolvement.NOT_INTERVIEWED]: 'Not Interviewed',
  [MasterNameInvolvement.OFFICER]: 'Officer',
  [MasterNameInvolvement.OTHER]: 'Other',
  [MasterNameInvolvement.OWNER]: 'Owner',
  [MasterNameInvolvement.PARENT]: 'Parent',
  [MasterNameInvolvement.POLICE_DEPARTMENT]: 'Police Department',
  [MasterNameInvolvement.PETITIONER]: 'Petitioner',
  [MasterNameInvolvement.PERSON_INTERVIEWED]: 'Person Interviewed',
  [MasterNameInvolvement.PERSON_REPORTING]: 'Person Reporting',
  [MasterNameInvolvement.PASSENGER]: 'Passenger',
  [MasterNameInvolvement.RESPONDENT]: 'Respondent',
  [MasterNameInvolvement.SCHOOL]: 'School',
  [MasterNameInvolvement.SEX_OFFENDER]: 'Sex Offender',
  [MasterNameInvolvement.SERVED_PERSON]: 'Served Person',
  [MasterNameInvolvement.SUSPECT]: 'Suspect',
  [MasterNameInvolvement.TEST]: 'Test',
  [MasterNameInvolvement.TOW_COMPANY]: 'Tow Company',
  [MasterNameInvolvement.VICTIM]: 'Victim',
  [MasterNameInvolvement.WITNESS]: 'Witness',
};

export const getKaseMasterNameInvolvementLabel = (value: string): string => {
  const label = KaseMasterNameInvolvementLabels[value as MasterNameInvolvement];
  return label ? `${value} - ${label}` : value;
};

export const getKaseMasterNameInvolvementItems = () =>
  Object.values(MasterNameInvolvement).map((value) => ({
    value,
    label: getKaseMasterNameInvolvementLabel(value),
  }));

// export enum KaseMasterOrganizaionInvolvement {
//   VICTIM = 'VI',
// }

export interface IKaseRejection {
  reason: string;
  rejectedBy: IUser;
  rejectedAt: Date;
}

export interface IApprover extends ObjectId {
  id: string;
  firstName: string;
  lastName: string;
}

export interface IApprovedInfo {
  approvedBy?: IApprover;
  approvedAt?: Date;
}

export interface IPenalCode extends IAuditModel {
  lawTitle: string;
  section: string;
  subsection: string;
  statutoryClass: string;
  category: string;
  degree: string;
  ibrCode?: string;
  ncicCode?: string;
  maxiLawDescription: string;
  miniLawDescription: string;
  lawDescription: string;
  longLawText?: string;
  offense?: string;
  lawOrdinal?: string;
  offenseCategory?: string;
  fullLawDescription?: string;
  expandedLawLiteral?: string;
  attemptedOffenseLawText?: string;
  completedOffenseLawText?: string;
  attemptedClass?: string;
  attemptedVfIndicator?: string;
  attemptedNysLawCategory?: string;
  busDriverChargeCode?: string;
  sexOffenderRegistryCode?: string;
  ucrCode?: string;
  safisCrimeCateoryCode?: string;
  joIndicator?: string;
  jdIndicator?: string;
  section13?: string;
  subSection13?: string;
  effectiveDate?: string;
  repealDate?: string;
  fpOffense?: string;
  unconstDate?: string;
  weaponCharge?: string;
  armedVfoCharge?: string;
  minorsCharge?: string;
  careerCriminalCharge?: string;
  insCharge?: string;
  nonSealCharge?: string;
  subConvictCharge?: string;
  jailCharge?: string;
  postConvictCharge?: string;
  autoStripCharge?: string;
  nysLawCategory?: string;
  vfIndicator?: string;
  dnaIndicator?: string;
  attemptedDnaIndicator?: string;
  escapeCharge?: string;
  hateCrime?: string;
  dateInvalidated?: string;
  terrorismIndicator?: string;
  dmvVtCode?: string;
  aoIndicator?: string;
  rtaFpOffense?: string;
  modifiedDate?: string;
  civilConfinementIndicator?: string;
  attemptedCci?: string;
  sexuallyMotivatedInd?: string;
  mcdvChargeIndicator?: string;
  rdlrIndicator?: string;
  spcCode?: string;
  stateNicsDisqualifier?: string;
}

export interface IOffenseForm extends ObjectId {
  attemptIndicator?: AttemptIndicator;
  count?: number;
  larcenyType?: string | null;
  locationType?: string | null;
  offenseLocation?: IAddress | null;
  weaponForces: string[];
  numberOfPremisesEntered?: string | null;
  methodOfEntry?: string | null;
  assaultHomicideCircumstances: string[];
  justifiableHomicideCircumstance?: string | null;
  offenderUsedComputerOrHandheldDevice?: string | null;
  criminalActivityGangInformationTypes: string[];
  ibrCode?: string;
  bond?: number;
  offenseStartDate?: string;
  offenseStartTime?: string;
  offenseEndDate?: string;
  offenseEndTime?: string;
}

export interface IOffense
  extends IAuditModel,
    Omit<IOffenseForm, 'offenderVictims'> {
  number?: number;
  penalCode?: IPenalCode;
  kase?: string;
  ibrCode?: string;
  offenderVictims: IOffenderVictim[];
  startDatetime: string;
  endDatetime?: string | null;
  timeZone: string;
}

export interface IArresteeOffense extends Omit<IOffense, 'kase'> {
  kase: {
    _id: string;
    number: string;
  };
}

export interface IOffenseRequest
  extends Omit<
    IOffenseForm,
    | 'offenseStartDate'
    | 'offenseStartTime'
    | 'offenseEndDate'
    | 'offenseEndTime'
    | 'offenseLocation'
  > {
  startDatetime: string;
  endDatetime?: string | null;
  timeZone: string;
  penalCode: string;
  offenderVictims: IOffenderVictimForm[];
  offenseLocation?: string | null;
}

export interface IPriorityForm {
  comment: string;
}

export interface IOffenderVictimForm {
  offender: string;
  victims: IVictimRelationshipForm[];
}

export interface IVictimRelationshipForm {
  victim: string;
  relationship: string;
}

export interface IVictimRelationship
  extends Omit<IVictimRelationshipForm, 'victim'> {
  victim: IMasterName;
}

export interface IOffenderVictim {
  offender: IMasterName;
  victims: IVictimRelationship[];
}

export interface IArrestChargeForm {
  penalCode?: string;
  attemptIndicator: AttemptIndicator;
  number?: string;
  count?: number;
  type?: string;
}

export interface IArrestFileForm {
  files: IFile[];
}

export interface IFileDescriptionForm {
  description: string;
}

export interface IArrestCharge extends Omit<IArrestChargeForm, 'penalCode'> {
  penalCode: IPenalCode;
}

export interface IArresteeForm {
  arrestLocation?: IAddress | string | null;
  arrestDate?: Date | null;
  arrestType?: string | null;
  arrestTypeCode?: string | null;
  arrestStatus?: string | null;
  arrestingOfficer?: string | null; // police user
  bookingOfficer?: string | null;
  condition?: string | null;
  attitude?: string | null;
  medicalAttention?: string | null;
  injury?: string | null;
  drugsNarcotics?: string | null;
  bloodAlcoholLevel?: string | null;
  juvenileReleaseStatus?: string | null;
  arresteeWeapons?: string[] | null;
  arrestLarcenyType?: string | null;
  arresteeResidence?: string | null;
  arresteeAgeGroup?: string | null;
  arresteeAge?: number | null;
  notes?: string | null;
  files?: IFile[] | null;
  arrestCharges?: IArrestChargeForm[] | null;

  // Administrative Information (NYS Arrest Report)
  nysidNumber?: string | null;
  obtsNumber?: string | null;
  refNumber?: string | null;
  fbiNumber?: string | null;
  division?: string | null;

  // Additional Officers
  assistingOfficer?: string | null;

  juvenile?: string | null;
  coDefendantsArrestNumber?: string[] | null;

  // Miranda Rights
  miranda?: string | null;
  mirandaBy?: string | null;
  mirandaDate?: Date | null;

  // Statements & Legal
  statements?: string | null;
  courtStatus?: string | null;
  searchWarrant?: string | null;
  idProcedure?: string | null;

  // Arraignment
  arraignmentCourt?: string | null;
  arraignmentJudge?: string | null;
  arraignmentDate?: Date | null;

  // Evidence & Property
  property?: string | null;
  evidence?: string | null;
  processedBy?: string | null;

  // Disposition & Status
  disposition?: string | null;
  incidentNumber?: string | null;
  bailAmount?: number | null;
  bondsman?: string | null;

  // Photo & Warrant
  photoNumber?: string | null;
  warrantNumber?: string | null;

  // Other Agency
  arrestForOtherAgency?: boolean | null;
  otherAgency?: string | null;
  fingerprintPictureTaken?: string | null;

  // Offense Details
  offenseAddress?: IAddress | string | null;
  offenseDate?: Date | null;

  // Return Court
  returnCourt?: string | null;
  returnJudge?: string | null;
  returnDate?: Date | null;

  // Transfer
  defendantCaseTurnedOverToAgency?: string | null;

  // Officer Details
  officerName?: string | null;
  arresteeTurnedOverDate?: Date | null;

  // Notes
  arrestNotes?: string | null;

  // Employment
  employed?: boolean | null;

  // Signatures
  arrestingOfficerSignature?: string | null;
  supervisorSignature?: string | null;
  safisLatentPrintIdentificationArrest?: string | null;
}

export interface IArrestee
  extends IAuditModel,
    Omit<
      IArresteeForm,
      | 'arrestingOfficer'
      | 'bookingOfficer'
      | 'arrestCharges'
      | 'assistingOfficer'
      | 'mirandaBy'
      | 'arraignmentCourt'
      | 'processedBy'
      | 'incidentNumber'
      | 'otherAgency'
      | 'returnCourt'
      | 'officerName'
    > {
  kaseId: string;
  masterName: IMasterName;
  number: string;
  arrestId?: string | null;
  arrestingOfficer?: IUser;
  bookingOfficer?: IUser;
  arrestCharges?: IArrestCharge[] | null;

  // Populated fields for NYS Arrest Report
  assistingOfficer?: IUser;
  mirandaBy?: IUser;
  arraignmentCourt?: IVendor;
  processedBy?: IUser;
  incidentNumber?: ICFS;
  otherAgency?: IAgency;
  returnCourt?: IVendor;
  officerName?: IUser;
  dataWorksStatus?: 'Created' | 'In Temp' | 'Ready for Booking' | 'Processed';
}

export interface IArresteeRequest
  extends Omit<IArresteeForm, 'arrestLocation'> {
  arrestLocation?: string;
  masterName?: Partial<IMasterNameReq>;
}

export enum GeographicType {
  NYS_COUNTY = 'NYS County',
  CITY = 'City',
  VILLAGE = 'Village',
  TOWN = 'Town',
  NYPD_PRECINCT = 'NYPD Precinct',
  INDIAN_TERRITORY = 'Indian Territory',
  FOREIGN_COUNTRY = 'Foreign Country',
  STATE = 'State',
}

export interface ICTVCode extends IAuditModel {
  code: string;
  geographicType: GeographicType;
  locationLiteral: string;
}

export interface IOffenseOptionItem {
  value: string;
  label: string;
  category: string;
  disable_rules: Array<string>;
  nibrsCode?: string;
  key?: string;
}

export interface IPopulateRuleItem {
  condition: string;
  values: string[];
}

export interface IValidateRuleItem {
  condition: string;
  validateReturn: string | boolean;
}

export interface IOffenseOption extends IAuditModel {
  title: string;
  type: string;
  category: string[];
  multiple?: boolean;
  disable_rules: Array<string>;
  populate_rules: Array<IPopulateRuleItem>;
  default_value_rules: Array<IPopulateRuleItem>;
  fixed_items?: Array<IPopulateRuleItem>;
  validate_rules: Array<IValidateRuleItem>;
  submit_rules?: Array<IPopulateRuleItem>;
  // items: Record<string, IOffenseOptionItem>;
  items: Map<string, IOffenseOptionItem>;
}

export interface IOffenseOptionResponse extends Omit<IOffenseOption, 'items'> {
  items: Array<IOffenseOptionItem>;
}

export type OffenseGenericMethod = <T>(
  currentValue: any,
  formValues: T,
) => boolean | string;

export type IOffenseDisabledMedhod = <T>(formValues: T) => boolean;

export type RuleValuesItemsMethod = <T>(formValues: T) => [];

export type IOffenseDynamicOptions<T> = {
  label?: string;
  items: any; //TODO: Replace `any` with the actual type of `items`
  setDisable: (formValues: T) => boolean | undefined;
  rules: {
    validate: any; // TODO: one day i should typecast this
  };
  fixedItems: (formValues: T) => Array<string>;
  submitRules: (formValues: T) => Array<string>;
};

export type IDynamicFormTypes =
  | 'ARREST'
  | 'DISPOSITION'
  | 'INCIDENT'
  | 'OFFENDER'
  | 'OFFENDER_VICTIM_RELATIONS'
  | 'OFFENSE'
  | 'PROPERTIES_DRUGS'
  | 'USER_INDENTITY'
  | 'VICTIM'
  | 'WITNESS'
  | 'PROPERTY_GENERAL';
